/**
 * MA-ARCH-201 Phase 3: Permission-Based Route Guard
 * 
 * Protects routes based on user's effective permissions.
 * Shows access denied page if user navigates to inaccessible route.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useHasModule, usePermissions } from '@/hooks/usePermissions';
import { useTenant } from '@/contexts/TenantContext';
import { AlertCircle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

/**
 * Access Denied Page
 * 
 * Shown when user navigates to a route they don't have permission for.
 */
export function AccessDenied({ moduleName }) {
  const navigate = useNavigate();
  
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center space-y-6 max-w-md px-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-red-500/10 p-4">
            <Lock className="h-12 w-12 text-red-500" />
          </div>
        </div>
        
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-foreground">
            Access Denied
          </h1>
          <p className="text-muted-foreground">
            {moduleName 
              ? `You don't have permission to access ${moduleName}.`
              : "You don't have permission to access this page."}
          </p>
          <p className="text-sm text-muted-foreground">
            Contact your account administrator if you need access.
          </p>
        </div>
        
        <div className="flex gap-3 justify-center">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
          >
            Go Back
          </Button>
          <Button
            // P4-WP-9A.1: '/dashboard' is not a route -- the dashboard entry
            // is a CHILD of /app, so this fell through to the top-level
            // catch-all and left the application entirely. '/app' reaches the
            // canonical resolver, which sends the caller somewhere they can
            // actually go rather than back to the page that just denied them.
            onClick={() => navigate('/app')}
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Module Route Guard
 * 
 * Wraps a route component and checks if user has access to the module.
 * Shows AccessDenied page if user doesn't have permission.
 * 
 * MA-ARCH-201 Phase 3: Backend permission system is authoritative.
 * MA-PHASE11A FIX: Wait for TenantContext before checking permissions.
 */
export function RequireModule({ moduleId, moduleName, children }) {
  const { hasAccess, isLoading: moduleLoading } = useHasModule(moduleId);
  const { hasActiveMembership, isLoading: permissionsLoading } = usePermissions();
  
  // MA-PHASE11A FIX: Import and check TenantContext loading state
  const { loading: tenantLoading } = useTenant();
  
  // Loading state - wait for TenantContext, module check, AND permissions context
  // This prevents "Restricted" flash before account context is resolved
  if (tenantLoading || moduleLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  // No active membership - redirect to login/onboarding
  if (!hasActiveMembership) {
    return <Navigate to="/activation" replace />;
  }
  
  // Access denied
  if (!hasAccess) {
    return <AccessDenied moduleName={moduleName} />;
  }
  
  // Access granted
  return <>{children}</>;
}

/**
 * Settings Section Route Guard
 * 
 * Similar to RequireModule but for settings sections.
 * MA-PHASE11A FIX: Wait for TenantContext before checking permissions.
 */
export function RequireSettingsSection({ sectionId, sectionName, children }) {
  const { effectiveSettings, isLoading: permissionsLoading, hasActiveMembership } = usePermissions();
  const { loading: tenantLoading } = useTenant();
  
  const hasAccess = effectiveSettings.includes(sectionId);
  
  // Loading state - wait for TenantContext AND permissions to fully load
  if (tenantLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  // No active membership
  if (!hasActiveMembership) {
    return <Navigate to="/activation" replace />;
  }
  
  // Access denied
  if (!hasAccess) {
    return <AccessDenied moduleName={sectionName || 'this settings section'} />;
  }
  
  // Access granted
  return <>{children}</>;
}

/**
 * MA-RBAC-BATCH3-FRONTEND-PERMISSION-INFRA-PHASE4-115B:
 * Level-Aware Module Route Guard
 * 
 * Protects routes based on user's module access level (read vs. full).
 * Prevents read-only users from accessing builder/admin routes.
 * 
 * Access levels:
 * - "off": No access (module not available)
 * - "read" or "view": Read-only access (can view, cannot mutate)
 * - "write": Can create/edit but not full admin
 * - "full": Full access including builder/admin features
 * 
 * @param {string} moduleId - Module to check (e.g., "profit_paths", "landing_pages")
 * @param {string} requiredLevel - Required access level ("read", "write", "full")
 * @param {string} moduleName - Display name for error messages
 * @param {ReactNode} children - Protected content
 */
export function RequireModuleLevel({ moduleId, requiredLevel = "full", moduleName, children }) {
  const { moduleAccessLevels, isLoading: permissionsLoading, hasActiveMembership } = usePermissions();
  const { loading: tenantLoading } = useTenant();
  
  // Get user's access level for this module
  const userAccessLevel = moduleAccessLevels[moduleId] || "off";
  
  // Define access level hierarchy
  const levelHierarchy = {
    "off": 0,
    "read": 1,
    "view": 1,
    "write": 2,
    "full": 3,
  };
  
  const userLevelRank = levelHierarchy[userAccessLevel] || 0;
  const requiredLevelRank = levelHierarchy[requiredLevel] || 3;
  
  const hasRequiredLevel = userLevelRank >= requiredLevelRank;
  
  // Loading state - wait for TenantContext AND permissions to fully load
  // This prevents permission flicker and premature access denial
  if (tenantLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  // No active membership - redirect to activation
  if (!hasActiveMembership) {
    return <Navigate to="/activation" replace />;
  }
  
  // Access denied - user doesn't have required level
  if (!hasRequiredLevel) {
    return <AccessDenied moduleName={moduleName} />;
  }
  
  // Access granted - user has required level
  return <>{children}</>;
}

/**
 * P4-A1: authorization projection for /activation.
 *
 * The route previously required only a token, so any authenticated member of
 * any Account could render ActivationPage by typing the URL -- hiding the
 * header badge alone would have left that open. The page's own APIs are
 * RequireModule("settings"), which WP-8A governance constrains to Account
 * Owner/Admin, so this projects exactly the authorization the backend already
 * enforces rather than restating it as a role test. A SuperAdmin operating in
 * a validated Account is covered for free: the access-context resolver
 * projects platform authority as Owner, so `settings` is present.
 *
 * It renders AccessDenied rather than redirecting, DELIBERATELY. The shared
 * guards in PermissionGuard send a caller with no active membership to
 * `/activation`; a redirecting guard on this very route would therefore bounce
 * such a caller between /activation and itself forever. Rendering terminates.
 */
export function RequireActivationAccess({ children }) {
  const { hasAccess, isLoading: moduleLoading } = useHasModule('settings');
  const { isLoading: permissionsLoading } = usePermissions();
  const { loading: tenantLoading } = useTenant();

  if (tenantLoading || moduleLoading || permissionsLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-[60vh]"
        data-testid="activation-access-loading"
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!hasAccess) return <AccessDenied moduleName="System Activation" />;

  return <>{children}</>;
}
